import { ChangeEvent, useCallback } from 'react'
import omit from 'lodash/omit'

import {
  BetweenDatesConditionTime,
  BetweenDatesConditionValue,
  FilterCondition,
} from 'common/filter/interfaces'
import { prepareDateTimeWithBotTimezoneForBackend } from 'common/usesList/utils'
import { SECONDS_IN_DAY } from 'components/DelayPicker/constants'
import { WEEKDAYS } from 'constants/constants'

export type Weekdays = typeof WEEKDAYS

export const useConditionValueHandlers = (
  onChange: (data: Partial<FilterCondition>, closePopover?: boolean) => void,
  condition: FilterCondition,
  botTimezone?: Nullable<string>,
) => {
  const handleChange = useCallback(
    (event: ChangeEvent<{ value: string | number }>, closePopover?: boolean) => {
      onChange({ value: event.target.value }, closePopover)
    },
    [onChange],
  )

  const handleCurrentTimeFromChange = useCallback(
    (event: ChangeEvent<{ value: BetweenDatesConditionTime['time'] }>) => {
      const value = {
        ...omit(condition.value as BetweenDatesConditionValue, 'time_zone'),
        from: { time: event.target.value },
      }
      onChange({ value })
    },
    [condition, onChange],
  )

  const handleCurrentTimeToChange = useCallback(
    (event: ChangeEvent<{ value: BetweenDatesConditionTime['time'] }>) => {
      const value = {
        ...omit(condition.value as BetweenDatesConditionValue, 'time_zone'),
        to: { time: event.target.value },
      }
      onChange({ value })
    },
    [condition, onChange],
  )

  const handleCurrentTimeWeekdaysChange = useCallback(
    (event: ChangeEvent<{ value: Weekdays }>) => {
      const value = {
        ...(condition.value as BetweenDatesConditionValue),
        weekdays: event.target.value,
      }
      onChange({ value })
    },
    [condition, onChange],
  )

  const handleChangeDatePicker = useCallback(
    (event: ChangeEvent<{ value: string }>, closePopover?: boolean) => {
      const value = botTimezone
        ? prepareDateTimeWithBotTimezoneForBackend(event.target.value, botTimezone)
        : event.target.value
      onChange({ value }, closePopover)
    },
    [botTimezone, onChange],
  )

  const handleDelayChange = useCallback(
    (value: number) => {
      onChange({ value: -1 * value, unit: 'seconds' })
    },
    [onChange],
  )

  const setDefaultOffset = useCallback(() => {
    onChange({ offset: { operator: '+', value: SECONDS_IN_DAY } }, false)
  }, [onChange])

  const resetOffset = useCallback(() => {
    onChange({ offset: null }, false)
  }, [onChange])

  const handleDelayCheckBoxChange = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) => {
      target.checked ? setDefaultOffset() : resetOffset()
    },
    [resetOffset, setDefaultOffset],
  )

  return {
    onChange: handleChange,
    onCurrentTimeFromChange: handleCurrentTimeFromChange,
    onCurrentTimeToChange: handleCurrentTimeToChange,
    onCurrentTimeWeekdaysChange: handleCurrentTimeWeekdaysChange,
    onChangeDatePicker: handleChangeDatePicker,
    onDelayChange: handleDelayChange,
    onDelayCheckBoxChange: handleDelayCheckBoxChange,
    setDefaultOffset,
    resetOffset,
  }
}
